// this file contains the filter for when properties are rendered in the price labels component

import type { A11yTextType } from '../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { PriceType } from '../../../../common/models/priceData/priceData';
import type { ProductPricePresentationSpec } from '../../../../ecommerce/components/ProductPresentationSpecs';
import type { PrepurchaseProductPriceData } from './PrepurchaseProductPriceLabels.types';

import { pickProductPricePromotion } from '../../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';

type Params = {
    spec: ProductPricePresentationSpec | 'primaryComparativePrice';
    product: PrepurchaseProductPriceData;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
};

type FilterParams = {
    displayComparativePrice: boolean;
};

type PickedPromotion = ReturnType<typeof pickProductPricePromotion>;

/*
    This function returns the appropriate spec filter based on mode and type. What labels should be shown in which context.
 */
export const getPrePurchaseSpecFilter = ({ displayComparativePrice }: FilterParams) => {
    return {
        primary: displayComparativePrice
            ? ComparativePricePrePurchasePriceFilter.primary
            : DefaultPrePurchasePriceFilter.primary,
        secondary: displayComparativePrice
            ? ComparativePricePrePurchasePriceFilter.secondary
            : DefaultPrePurchasePriceFilter.secondary,
    };
};

/** PIL hide for Ord. pris / Ord. jfr pris when history exists; exempt multi-buy, max-use, member, B2B. */
const shouldHideRegularPriceDueToHistoricalPil = (params: Params, picked: PickedPromotion) =>
    !!params.product.historicalPriceData &&
    picked.type !== 'multiBuy' &&
    !picked.promotion?.maxUseText &&
    !picked.promotion?.isMemberPrice &&
    !params.isB2B;

const DefaultPrePurchasePriceFilter = {
    primary: (params: Params): boolean => {
        // Add primary price specific filters here if needed in the future
        if (params.spec === 'primaryComparativePrice') {
            // only show primary comparative price if comparative price is available
            return false;
        }
        return true;
    },
    secondary: (params: Params): boolean => {
        const picked = pickProductPricePromotion({
            promo: params.product.promotion,
            checkUserMemberContext: false,
        });

        // EDLP secondary: Ord. pris + Ord. jfr pris + promo jfr (plain wording in renderer).
        if (picked.type === 'edlp') {
            return (
                params.spec === 'originalPrice' ||
                params.spec === 'comparativePrice' ||
                params.spec === 'promoComparativePrice' ||
                params.spec === 'recycleFee' ||
                params.spec === 'excludingTax' ||
                params.spec === 'maxPromoUsage'
            );
        }

        // Hide historical price if no historical price is available
        switch (params.spec) {
            case 'maxPromoUsage':
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }
                break;
            case 'originalPrice':
                // Hide Ord. pris / Ord. jfr pris when historical data exists (PIL), except max-use,
                // member promo, multi-buy (WEBECOM-2705), or B2B — see shouldHideRegularPriceDueToHistoricalPil.
                if (
                    shouldHideRegularPriceDueToHistoricalPil(params, picked) ||
                    !!picked.promotion?.higherThanHistoricalPrice
                ) {
                    return false;
                }

                switch (picked.type) {
                    /* Show original price ONLY when any promotion is applicable */
                    case 'fixed':
                    case 'percentage':
                    case 'multiBuy':
                        return true;
                    default:
                }
                return false;
            case 'comparativePrice':
                // Hide Ord. pris / Ord. jfr pris when historical data exists (PIL), except max-use,
                // member promo, multi-buy (WEBECOM-2705), or B2B — see shouldHideRegularPriceDueToHistoricalPil.
                if (shouldHideRegularPriceDueToHistoricalPil(params, picked)) {
                    return false;
                }

                break;
            case 'historicalPrice':
                if (params.isB2B || picked.type === 'multiBuy') {
                    // B2B specific logic for historical price
                    return false;
                }
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }
                break;
            case 'promoComparativePrice':
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }
                break;
            case 'primaryPrice':
                return false;

            default:
        }
        return true;
    },
};

const ComparativePricePrePurchasePriceFilter = {
    primary: (params: Params): boolean => {
        if (params.spec === 'primaryPrice') {
            // only show primary price if weight price is available
            return false;
        }
        // Add primary price specific filters here if needed in the future
        return true;
    },
    secondary: (params: Params): boolean => {
        const picked = pickProductPricePromotion({
            promo: params.product.promotion,
            checkUserMemberContext: false,
        });

        // EDLP secondary: Ord. pris + Ord. jfr pris + promo jfr (plain wording in renderer).
        if (picked.type === 'edlp') {
            return (
                params.spec === 'originalPrice' ||
                params.spec === 'comparativePrice' ||
                params.spec === 'promoComparativePrice' ||
                params.spec === 'recycleFee' ||
                params.spec === 'excludingTax' ||
                params.spec === 'maxPromoUsage'
            );
        }

        // Hide historical price if no historical price is available
        switch (params.spec) {
            case 'maxPromoUsage':
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }

                break;
            case 'originalPrice':
                // Hide Ord. pris / Ord. jfr pris when historical data exists (PIL), except max-use,
                // member promo, multi-buy (WEBECOM-2705), or B2B — see shouldHideRegularPriceDueToHistoricalPil.
                if (
                    shouldHideRegularPriceDueToHistoricalPil(params, picked) ||
                    picked.promotion?.higherThanHistoricalPrice
                ) {
                    return false;
                }

                switch (picked.type) {
                    /* Show original price ONLY when any promotion is applicable */
                    case 'fixed':
                    case 'percentage':
                    case 'multiBuy':
                        return true;
                    default:
                }
                return false;
            case 'comparativePrice':
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }

                if (params.product.historicalPriceData && picked.promotion?.maxUseText) {
                    return true;
                }
                // Multi-buys always show Ord. jfr pris (WEBECOM-2705), even with price history.
                if (picked.type === 'multiBuy') {
                    return true;
                }
                if (picked.type === 'fixed' || picked.type === 'percentage') {
                    if (!params.product.historicalPriceData) {
                        return true;
                    }
                }

                return false;

            case 'historicalPrice':
                if (params.isB2B || picked.type === 'multiBuy') {
                    // B2B specific logic for historical price
                    return false;
                }
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return false;
                }
                break;
            case 'promoComparativePrice':
                if (!picked.promotion?.higherThanHistoricalPrice && picked.type === 'multiBuy') {
                    return true;
                }
                return false;
            case 'primaryPrice':
                // primary price is always shown in secondary group as well
                if (!picked.promotion?.higherThanHistoricalPrice && picked.type === 'multiBuy') {
                    return false;
                }
                return true;
            default:
        }
        return true;
    },
};
